// src/pages/CreateUserModalPage.js
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import Select from "react-select";

import {
  FiAtSign,
  FiCalendar,
  FiCreditCard,
  FiEye,
  FiEyeOff,
  FiGlobe,
  FiHome,
  FiLock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiShield,
  FiTrash2,
  FiUpload,
  FiUser,
  FiUserPlus,
  FiX,
} from "react-icons/fi";

import axios from "../axiosConfig";

import "../styles/CreateUserModalPage.css";

const ROLE_KEYS = [
  "admin",
  "manager",
  "dispatcher",
  "accountant"
];

const MAX_FILE_SIZE =
  5 * 1024 * 1024;

const createUserSelectStyles = {
  control: (
    base,
    state
  ) => ({
    ...base,

    minHeight: 42,
    height: 42,

    borderRadius: 10,

    paddingLeft: 2,

    background:
      "var(--cu-field-bg)",

    borderColor:
      state.selectProps
        .isInvalid
        ? "var(--cu-danger)"
        : state.isFocused
          ? "var(--cu-primary)"
          : "var(--cu-border)",

    boxShadow:
      state.isFocused
        ? "0 0 0 3px var(--cu-focus-halo)"
        : state.selectProps
              .isInvalid
          ? "0 0 0 2px var(--cu-danger-halo)"
          : "none",

    "&:hover": {
      borderColor:
        state.selectProps
          .isInvalid
          ? "var(--cu-danger)"
          : state.isFocused
            ? "var(--cu-primary)"
            : "var(--cu-border-strong)",
    },

    cursor:
      "pointer",
  }),

  valueContainer: (
    base
  ) => ({
    ...base,

    height: 42,

    padding:
      "0 11px",
  }),

  placeholder: (
    base
  ) => ({
    ...base,

    color:
      "var(--cu-placeholder)",

    fontSize: 13,
  }),

  singleValue: (
    base
  ) => ({
    ...base,

    color:
      "var(--cu-text)",

    fontSize: 13,
  }),

  input: (
    base
  ) => ({
    ...base,

    color:
      "var(--cu-text)",

    fontSize: 13,
  }),

  indicatorsContainer: (
    base
  ) => ({
    ...base,

    height: 42,
  }),

  indicatorSeparator: () => ({
    display:
      "none",
  }),

  clearIndicator: (
    base
  ) => ({
    ...base,

    color:
      "var(--cu-muted)",

    padding: 6,

    ":hover": {
      color:
        "var(--cu-text)",
    },
  }),

  dropdownIndicator: (
    base,
    state
  ) => ({
    ...base,

    color:
      state.isFocused
        ? "var(--cu-text)"
        : "var(--cu-muted)",

    padding:
      "0 10px 0 6px",

    ":hover": {
      color:
        "var(--cu-text)",
    },
  }),

  menu: (
    base
  ) => ({
    ...base,

    zIndex: 50,

    marginTop: 7,

    overflow:
      "hidden",

    border:
      "1px solid var(--cu-border)",

    borderRadius:
      11,

    background:
      "var(--cu-panel-solid)",

    boxShadow:
      "0 18px 42px rgba(0, 0, 0, 0.34)",
  }),

  menuList: (
    base
  ) => ({
    ...base,

    padding: 6,
  }),

  option: (
    base,
    state
  ) => ({
    ...base,

    borderRadius:
      8,

    color:
      "var(--cu-text)",

    backgroundColor:
      state.isSelected
        ? "var(--cu-primary-soft)"
        : state.isFocused
          ? "var(--cu-field-hover)"
          : "transparent",

    fontSize:
      13,

    cursor:
      "pointer",

    ":active": {
      backgroundColor:
        "var(--cu-primary-soft-strong)",
    },
  }),
};

function FieldError({
  value,
  fallback,
}) {
  if (!value) {
    return null;
  }

  return (
    <p className="create-user-error">
      {typeof value ===
      "string"
        ? value
        : fallback}
    </p>
  );
}

function FieldLabel({
  children,
  required = false,
  icon: Icon,
}) {
  return (
    <label className="create-user-field-label">
      {Icon && (
        <Icon
          className="create-user-field-label-icon"
          aria-hidden="true"
        />
      )}

      <span className="create-user-field-label-text">
        {children}

        {required && (
          <span
            className="create-user-required"
            aria-hidden="true"
          >
            *
          </span>
        )}
      </span>
    </label>
  );
}

export default function CreateUserModalPage({
  isOpen = true,
  onClose,
  onCreated,
}) {
  const { t } =
    useTranslation(
      "create_user_modal_page"
    );

  const navigate =
    useNavigate();

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const [
    invalid,
    setInvalid,
  ] = useState({});

  const [
    role,
    setRole,
  ] = useState(null);

  const [
    showPassword,
    setShowPassword,
  ] = useState(false);

  const [
    showRepeatPassword,
    setShowRepeatPassword,
  ] = useState(false);

  const [
    form,
    setForm,
  ] = useState({
    name: "",
    username: "",
    email: "",
    phoneNumber: "",
    password: "",
    repeatPassword: "",
    idNumber: "",
    idExpiryDate: "",
    dateOfBirth: "",
    country: "",
    city: "",
    postalCode: "",
    address: "",
  });

  const [
    profilePreview,
    setProfilePreview,
  ] = useState(null);

  const [
    profileFile,
    setProfileFile,
  ] = useState(null);

  const [
    profileEditorOpen,
    setProfileEditorOpen,
  ] = useState(false);

  const [
    profileZoom,
    setProfileZoom,
  ] = useState(1);

  const [
    profilePositionX,
    setProfilePositionX,
  ] = useState(50);

  const [
    profilePositionY,
    setProfilePositionY,
  ] = useState(50);

  const [
    adjustedProfileFile,
    setAdjustedProfileFile,
  ] = useState(null);

  const [
    idPreview,
    setIdPreview,
  ] = useState(null);

  const profileFileRef =
    useRef(null);

  const idFileRef =
    useRef(null);

  const dobRef =
    useRef(null);

  const idExpRef =
    useRef(null);

  const closeModal =
    useCallback(
      () => {
        if (onClose) {
          onClose();
          return;
        }

        navigate(
          "/settings/users"
        );
      },
      [
        navigate,
        onClose,
      ]
    );

  const clearFieldError = (
    key
  ) => {
    setInvalid(
      (
        previous
      ) => {
        if (
          !previous[key] &&
          !previous._server
        ) {
          return previous;
        }

        return {
          ...previous,

          [key]:
            false,

          _server:
            undefined,
        };
      }
    );
  };

  const set = (
    key,
    value
  ) => {
    setForm(
      (
        previous
      ) => ({
        ...previous,

        [key]:
          value,
      })
    );

    clearFieldError(
      key
    );
  };

  const openPicker = (
    ref
  ) => {
    if (
      ref?.current
        ?.showPicker
    ) {
      ref.current.showPicker();
      return;
    }

    ref?.current?.focus();
  };

  const pickFile = (
    ref
  ) => {
    ref.current?.click();
  };

  const replacePreview = (
    file,
    setPreview
  ) => {
    setPreview(
      (
        previous
      ) => {
        if (previous) {
          URL.revokeObjectURL(
            previous
          );
        }

        return file
          ? URL.createObjectURL(
              file
            )
          : null;
      }
    );
  };

  const handleProfileFile = (
    event
  ) => {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    const isImage =
      /^image\//i.test(
        file.type
      ) ||
      /\.(heic|heif)$/i.test(
        file.name
      );

    if (!isImage) {
      setInvalid(
        (
          previous
        ) => ({
          ...previous,

          profilePicture:
            t(
              "errors.invalid_file_profile"
            ),
        })
      );

      event.target.value =
        "";

      return;
    }

    if (
      file.size >
      MAX_FILE_SIZE
    ) {
      setInvalid(
        (
          previous
        ) => ({
          ...previous,

          profilePicture:
            t(
              "errors.file_too_large"
            ),
        })
      );

      event.target.value =
        "";

      return;
    }

    clearFieldError(
      "profilePicture"
    );

    setProfileFile(
      file
    );

    setAdjustedProfileFile(
      null
    );

    setProfileZoom(
      1
    );

    setProfilePositionX(
      50
    );

    setProfilePositionY(
      50
    );

    replacePreview(
      file,
      setProfilePreview
    );

    setProfileEditorOpen(
      true
    );
  };

  const handleIdFile = (
    event
  ) => {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    const isAllowed =
      /^image\//i.test(
        file.type
      ) ||
      file.type ===
        "application/pdf" ||
      /\.(heic|heif)$/i.test(
        file.name
      );

    if (!isAllowed) {
      setInvalid(
        (
          previous
        ) => ({
          ...previous,

          idPhoto:
            t(
              "errors.invalid_file_id"
            ),
        })
      );

      event.target.value =
        "";

      return;
    }

    if (
      file.size >
      MAX_FILE_SIZE
    ) {
      setInvalid(
        (
          previous
        ) => ({
          ...previous,

          idPhoto:
            t(
              "errors.file_too_large"
            ),
        })
      );

      event.target.value =
        "";

      return;
    }

    clearFieldError(
      "idPhoto"
    );

    replacePreview(
      file,
      setIdPreview
    );
  };

  const removeProfilePhoto =
    () => {
      if (
        profilePreview
      ) {
        URL.revokeObjectURL(
          profilePreview
        );
      }

      setProfilePreview(
        null
      );

      setProfileFile(
        null
      );

      setAdjustedProfileFile(
        null
      );

      setProfileEditorOpen(
        false
      );

      setProfileZoom(
        1
      );

      setProfilePositionX(
        50
      );

      setProfilePositionY(
        50
      );

      if (
        profileFileRef.current
      ) {
        profileFileRef.current.value =
          "";
      }

      clearFieldError(
        "profilePicture"
      );
    };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const onEsc = (
      event
    ) => {
      if (
        event.key ===
        "Escape"
      ) {
        closeModal();
      }
    };

    document.addEventListener(
      "keydown",
      onEsc
    );

    return () => {
      document.removeEventListener(
        "keydown",
        onEsc
      );
    };
  }, [
    closeModal,
    isOpen,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style
        .overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [isOpen]);

  useEffect(
    () => () => {
      if (
        profilePreview
      ) {
        URL.revokeObjectURL(
          profilePreview
        );
      }
    },
    [profilePreview]
  );

  useEffect(
    () => () => {
      if (
        idPreview
      ) {
        URL.revokeObjectURL(
          idPreview
        );
      }
    },
    [idPreview]
  );

  const validate =
    () => {
      const errors =
        {};

      const required = [
        "name",
        "username",
        "email",
        "phoneNumber",
        "password",
        "repeatPassword",
        "idNumber",
        "idExpiryDate",
        "dateOfBirth",
        "country",
        "city",
        "postalCode",
        "address",
      ];

      required.forEach(
        (key) => {
          if (
            !String(
              form[key] ||
                ""
            ).trim()
          ) {
            errors[key] =
              t(
                "errors.required"
              );
          }
        }
      );

      if (!role) {
        errors.role =
          t(
            "errors.required"
          );
      }

      if (
        form.password &&
        form.password
          .length < 8
      ) {
        errors.password =
          t(
            "errors.password_min"
          );
      }

      if (
        form.password !==
        form.repeatPassword
      ) {
        errors.repeatPassword =
          t(
            "errors.password_mismatch"
          );
      }

      setInvalid(
        errors
      );

      return (
        Object.keys(
          errors
        ).length === 0
      );
    };

  const createAdjustedProfileFile =
    async () => {
      if (
        !profileFile ||
        !profilePreview
      ) {
        return null;
      }

      const image =
        await new Promise(
          (
            resolve,
            reject
          ) => {
            const img =
              new Image();

            img.onload =
              () =>
                resolve(
                  img
                );

            img.onerror =
              reject;

            img.src =
              profilePreview;
          }
        );

      const outputSize =
        512;

      const canvas =
        document.createElement(
          "canvas"
        );

      canvas.width =
        outputSize;

      canvas.height =
        outputSize;

      const ctx =
        canvas.getContext(
          "2d"
        );

      const imageWidth =
        image.naturalWidth;

      const imageHeight =
        image.naturalHeight;

      const baseCropSize =
        Math.min(
          imageWidth,
          imageHeight
        );

      const cropSize =
        baseCropSize /
        profileZoom;

      const maxX =
        imageWidth -
        cropSize;

      const maxY =
        imageHeight -
        cropSize;

      const sourceX =
        maxX *
        (
          profilePositionX /
          100
        );

      const sourceY =
        maxY *
        (
          profilePositionY /
          100
        );

      ctx.drawImage(
        image,
        sourceX,
        sourceY,
        cropSize,
        cropSize,
        0,
        0,
        outputSize,
        outputSize
      );

      const blob =
        await new Promise(
          (
            resolve
          ) => {
            canvas.toBlob(
              resolve,
              "image/jpeg",
              0.92
            );
          }
        );

      if (!blob) {
        return null;
      }

      return new File(
        [
          blob,
        ],
        "profile-picture.jpg",
        {
          type:
            "image/jpeg",
        }
      );
    };
  
  const confirmProfileAdjustment =
    async () => {
      const adjustedFile =
        await createAdjustedProfileFile();

      if (!adjustedFile) {
        return;
      }

      setAdjustedProfileFile(
        adjustedFile
      );

      replacePreview(
        adjustedFile,
        setProfilePreview
      );

      setProfileEditorOpen(
        false
      );
    };

  const handleSubmit =
    async (
      event
    ) => {
      event?.preventDefault?.();

      if (!validate()) {
        return;
      }

      try {
        setSubmitting(
          true
        );

        const fd =
          new FormData();

        fd.append(
          "name",
          form.name.trim()
        );

        fd.append(
          "username",
          form.username.trim()
        );

        fd.append(
          "email",
          form.email
            .trim()
            .toLowerCase()
        );

        fd.append(
          "phoneNumber",
          form.phoneNumber.trim()
        );

        fd.append(
          "password",
          form.password
        );

        fd.append(
          "role",
          role?.value ||
            ""
        );

        fd.append(
          "idNumber",
          form.idNumber.trim()
        );

        fd.append(
          "idExpiryDate",
          form.idExpiryDate
        );

        fd.append(
          "dateOfBirth",
          form.dateOfBirth
        );

        fd.append(
          "country",
          form.country.trim()
        );

        fd.append(
          "city",
          form.city.trim()
        );

        fd.append(
          "postalCode",
          form.postalCode.trim()
        );

        fd.append(
          "address",
          form.address.trim()
        );

        const finalProfileFile =
          adjustedProfileFile ||
          profileFileRef
            .current
            ?.files?.[0];

        if (
          finalProfileFile
        ) {
          fd.append(
            "profilePicture",
            finalProfileFile
          );
        }

        if (
          idFileRef.current
            ?.files?.[0]
        ) {
          fd.append(
            "idPhoto",
            idFileRef
              .current
              .files[0]
          );
        }

        const {
          data,
        } =
          await axios.post(
            "/users",
            fd,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        onCreated?.(
          data
        );

        closeModal();
      } catch (err) {
        console.error(
          "❌ Error creating user:",
          err
        );

        const response =
          err?.response;

        const status =
          response?.status;

        const serverError =
          response?.data
            ?.error ||
          t(
            "errors.server"
          );

        const details =
          response?.data
            ?.details;

        const next = {
          _server:
            serverError,
        };

        if (
          /Invalid file type/i.test(
            serverError
          )
        ) {
          next.profilePicture =
            t(
              "errors.invalid_file_profile"
            );

          next.idPhoto =
            t(
              "errors.invalid_file_id"
            );
        }

        if (
          status === 403
        ) {
          next.role =
            t(
              "errors.no_permission_role"
            );

          next._server =
            t(
              "errors.no_permission_role"
            );
        }

        if (
          status === 409
        ) {
          if (
            /username/i.test(
              serverError
            )
          ) {
            next.username =
              t(
                "errors.username_taken"
              );
          }

          if (
            /email/i.test(
              serverError
            )
          ) {
            next.email =
              t(
                "errors.email_taken"
              );
          }
        }

        if (
          Array.isArray(
            details
          )
        ) {
          for (
            const detail of
            details
          ) {
            const rawKey =
              detail
                ?.context
                ?.key ||
              detail
                ?.path?.[0];

            if (!rawKey) {
              continue;
            }

            let key =
              rawKey;

            if (
              rawKey ===
              "confirmPassword"
            ) {
              key =
                "repeatPassword";
            }

            let message =
              detail
                ?.message ||
              t(
                "errors.invalid_value"
              );

            if (
              key ===
                "repeatPassword" &&
              /match password/i.test(
                message
              )
            ) {
              message =
                t(
                  "errors.password_mismatch"
                );
            }

            if (
              (
                key ===
                  "idExpiryDate" ||
                key ===
                  "dateOfBirth"
              ) &&
              /must be a valid date/i.test(
                message
              )
            ) {
              message =
                t(
                  "errors.invalid_date"
                );
            }

            if (
              /is required/i.test(
                message
              ) &&
              key !==
                "repeatPassword"
            ) {
              message =
                t(
                  "errors.required"
                );
            }

            next[key] =
              message;
          }
        }

        setInvalid(
          (
            previous
          ) => ({
            ...previous,
            ...next,
          })
        );
      } finally {
        setSubmitting(
          false
        );
      }
    };

  const inputClass = (
    key
  ) =>
    `create-user-input ${
      invalid[key]
        ? "input-error"
        : ""
    }`;

  if (!isOpen) {
    return null;
  }

  const idFile =
    idFileRef.current
      ?.files?.[0] ||
    null;

  return (
    <div
      className="create-user-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-user-title"
      onMouseDown={(
        event
      ) => {
        if (
          event.target ===
          event.currentTarget
        ) {
          closeModal();
        }
      }}
    >
      <div
        className="create-user-modal"
        onMouseDown={(
          event
        ) =>
          event.stopPropagation()
        }
      >
        <header className="create-user-header">
          <div className="create-user-header-copy">
            <h2 id="create-user-title">
              {t("title")}
            </h2>

            <p>
              {t(
                "subtitle"
              )}
            </p>
          </div>

          <button
            type="button"
            className="create-user-close"
            onClick={
              closeModal
            }
            aria-label={t(
              "actions.close"
            )}
            title={t(
              "actions.close"
            )}
          >
            <FiX />
          </button>
        </header>

        <form
          className="create-user-form"
          onSubmit={
            handleSubmit
          }
        >
          <div className="create-user-body">
            {/* ==================================================
                LEFT — PROFILE PHOTO
               ================================================== */}

            <aside className="create-user-profile">
              <div className="create-user-profile-sticky">
                <button
                  type="button"
                  className={`create-user-avatar ${
                    invalid.profilePicture
                      ? "input-error"
                      : ""
                  }`}
                  onClick={() =>
                    pickFile(
                      profileFileRef
                    )
                  }
                  aria-label={t(
                    "photo.aria"
                  )}
                  title={t(
                    "photo.aria"
                  )}
                >
                  <span className="create-user-avatar-inner">
                    {profilePreview ? (
                      <img
                        src={
                          profilePreview
                        }
                        alt={t(
                          "photo.preview_alt"
                        )}
                      />
                    ) : (
                      <span className="create-user-avatar-placeholder">
                        <FiUser />

                        <span className="create-user-avatar-plus">
                          <FiUserPlus />
                        </span>
                      </span>
                    )}
                  </span>
                </button>

                <input
                  ref={
                    profileFileRef
                  }
                  type="file"
                  accept="image/*,.heic,.heif"
                  className="create-user-hidden-input"
                  onChange={
                    handleProfileFile
                  }
                />

                <div className="create-user-photo-copy">
                  <strong>
                    {profilePreview
                      ? t(
                          "photo.change"
                        )
                      : t(
                          "photo.add"
                        )}
                  </strong>

                  <span>
                    {t(
                      "photo.formats"
                    )}
                  </span>

                  <span>
                    {t(
                      "photo.max_size"
                    )}
                  </span>
                </div>

                {profilePreview && (
                  <div className="create-user-photo-actions">
                    <button
                      type="button"
                      className="create-user-photo-link"
                      onClick={() =>
                        setProfileEditorOpen(
                          true
                        )
                      }
                    >
                      {t(
                        "photo.adjust"
                      )}
                    </button>

                    <button
                      type="button"
                      className="create-user-photo-link"
                      onClick={() =>
                        pickFile(
                          profileFileRef
                        )
                      }
                    >
                      {t(
                        "photo.change"
                      )}
                    </button>

                    <button
                      type="button"
                      className="create-user-photo-link is-danger"
                      onClick={
                        removeProfilePhoto
                      }
                    >
                      <FiTrash2 />

                      {t(
                        "photo.remove"
                      )}
                    </button>
                  </div>
                )}

                <FieldError
                  value={
                    invalid.profilePicture
                  }
                  fallback={t(
                    "errors.invalid_file"
                  )}
                />
              </div>
            </aside>

          {profileEditorOpen &&
            profilePreview && (
              <div className="create-user-photo-editor-backdrop">
                <div className="create-user-photo-editor">
                  <div className="create-user-photo-editor-header">
                    <div>
                      <h3>
                        {t(
                          "photo.editor.title"
                        )}
                      </h3>

                      <p>
                        {t(
                          "photo.editor.subtitle"
                        )}
                      </p>
                    </div>

                    <button
                      type="button"
                      className="create-user-photo-editor-close"
                      onClick={() =>
                        setProfileEditorOpen(
                          false
                        )
                      }
                      aria-label={t(
                        "actions.close"
                      )}
                      title={t(
                        "actions.close"
                      )}
                    >
                      <FiX />
                    </button>
                  </div>

                  <div className="create-user-photo-editor-preview">
                    <div className="create-user-photo-editor-circle">
                      <img
                        src={
                          profilePreview
                        }
                        alt={t(
                          "photo.preview_alt"
                        )}
                        style={{
                          objectPosition: `${profilePositionX}% ${profilePositionY}%`,
                          transform: `scale(${profileZoom})`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="create-user-photo-editor-controls">
                    <label>
                      <span>
                        {t(
                          "photo.editor.zoom"
                        )}
                      </span>

                      <input
                        type="range"
                        min="1"
                        max="3"
                        step="0.01"
                        value={
                          profileZoom
                        }
                        onChange={(
                          event
                        ) =>
                          setProfileZoom(
                            Number(
                              event
                                .target
                                .value
                            )
                          )
                        }
                      />
                    </label>

                    <label>
                      <span>
                        {t(
                          "photo.editor.horizontal"
                        )}
                      </span>

                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={
                          profilePositionX
                        }
                        onChange={(
                          event
                        ) =>
                          setProfilePositionX(
                            Number(
                              event
                                .target
                                .value
                            )
                          )
                        }
                      />
                    </label>

                    <label>
                      <span>
                        {t(
                          "photo.editor.vertical"
                        )}
                      </span>

                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={
                          profilePositionY
                        }
                        onChange={(
                          event
                        ) =>
                          setProfilePositionY(
                            Number(
                              event
                                .target
                                .value
                            )
                          )
                        }
                      />
                    </label>
                  </div>

                  <div className="create-user-photo-editor-actions">
                    <button
                      type="button"
                      className="create-user-btn"
                      onClick={() =>
                        setProfileEditorOpen(
                          false
                        )
                      }
                    >
                      {t(
                        "actions.cancel"
                      )}
                    </button>

                    <button
                      type="button"
                      className="create-user-btn create-user-btn-primary"
                      onClick={
                        confirmProfileAdjustment
                      }
                    >
                      {t(
                        "photo.editor.use_photo"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ==================================================
                RIGHT — FORM
               ================================================== */}

            <main className="create-user-main">
              {/* Row 1 */}

              <div className="create-user-row three">
                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiUser}
                  >
                    {t(
                      "fields.name.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiUser
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type="text"
                      value={
                        form.name
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "name",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "name"
                      )} has-leading-icon`}
                      placeholder={t(
                        "fields.name.placeholder"
                      )}
                      autoComplete="name"
                    />
                  </div>

                  <FieldError
                    value={
                      invalid.name
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>

                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiAtSign}
                  >
                    {t(
                      "fields.username.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiAtSign
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type="text"
                      value={
                        form.username
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "username",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "username"
                      )} has-leading-icon`}
                      placeholder={t(
                        "fields.username.placeholder"
                      )}
                      autoComplete="username"
                    />
                  </div>

                  <FieldError
                    value={
                      invalid.username
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>

                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiShield}
                  >
                    {t(
                      "fields.role.label"
                    )}
                  </FieldLabel>

                  <Select
                    isClearable
                    classNamePrefix="create-user"
                    styles={
                      createUserSelectStyles
                    }
                    options={ROLE_KEYS.map(
                      (
                        key
                      ) => ({
                        value:
                          key,

                        label:
                          t(
                            `roles.${key}`
                          ),
                      })
                    )}
                    value={
                      role
                    }
                    onChange={(
                      option
                    ) => {
                      setRole(
                        option
                      );

                      clearFieldError(
                        "role"
                      );
                    }}
                    isInvalid={
                      !!invalid.role
                    }
                    placeholder={t(
                      "fields.role.placeholder"
                    )}
                  />

                  <FieldError
                    value={
                      invalid.role
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>
              </div>

              {/* Row 2 */}

              <div className="create-user-row three">
                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiMail}
                  >
                    {t(
                      "fields.email.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiMail
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type="email"
                      value={
                        form.email
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "email",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "email"
                      )} has-leading-icon`}
                      placeholder={t(
                        "fields.email.placeholder"
                      )}
                      autoComplete="email"
                    />
                  </div>

                  <FieldError
                    value={
                      invalid.email
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>

                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiLock}
                  >
                    {t(
                      "fields.password.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiLock
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type={
                        showPassword
                          ? "text"
                          : "password"
                      }
                      value={
                        form.password
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "password",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "password"
                      )} has-leading-icon has-trailing-action`}
                      placeholder={t(
                        "fields.password.placeholder"
                      )}
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="create-user-input-action"
                      onClick={() =>
                        setShowPassword(
                          (
                            value
                          ) =>
                            !value
                        )
                      }
                      aria-label={t(
                        showPassword
                          ? "aria.hide_password"
                          : "aria.show_password"
                      )}
                    >
                      {showPassword ? (
                        <FiEyeOff />
                      ) : (
                        <FiEye />
                      )}
                    </button>
                  </div>

                  <FieldError
                    value={
                      invalid.password
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>

                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiLock}
                  >
                    {t(
                      "fields.repeatPassword.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiLock
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type={
                        showRepeatPassword
                          ? "text"
                          : "password"
                      }
                      value={
                        form.repeatPassword
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "repeatPassword",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "repeatPassword"
                      )} has-leading-icon has-trailing-action`}
                      placeholder={t(
                        "fields.repeatPassword.placeholder"
                      )}
                      autoComplete="new-password"
                    />

                    <button
                      type="button"
                      className="create-user-input-action"
                      onClick={() =>
                        setShowRepeatPassword(
                          (
                            value
                          ) =>
                            !value
                        )
                      }
                      aria-label={t(
                        showRepeatPassword
                          ? "aria.hide_password"
                          : "aria.show_password"
                      )}
                    >
                      {showRepeatPassword ? (
                        <FiEyeOff />
                      ) : (
                        <FiEye />
                      )}
                    </button>
                  </div>

                  <FieldError
                    value={
                      invalid.repeatPassword
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>
              </div>

              {/* Row 3 */}

              <div className="create-user-row two">
                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiPhone}
                  >
                    {t(
                      "fields.phoneNumber.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiPhone
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type="tel"
                      value={
                        form.phoneNumber
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "phoneNumber",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "phoneNumber"
                      )} has-leading-icon`}
                      placeholder={t(
                        "fields.phoneNumber.placeholder"
                      )}
                      autoComplete="tel"
                    />
                  </div>

                  <FieldError
                    value={
                      invalid.phoneNumber
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>

                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiCalendar}
                  >
                    {t(
                      "fields.dateOfBirth.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiCalendar
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      ref={
                        dobRef
                      }
                      type="date"
                      value={
                        form.dateOfBirth
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "dateOfBirth",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "dateOfBirth"
                      )} has-leading-icon has-trailing-action`}
                    />

                    <button
                      type="button"
                      className="create-user-input-action"
                      onClick={() =>
                        openPicker(
                          dobRef
                        )
                      }
                      aria-label={t(
                        "aria.open_calendar"
                      )}
                    >
                      <FiCalendar />
                    </button>
                  </div>

                  <FieldError
                    value={
                      invalid.dateOfBirth
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>
              </div>

              {/* Row 4 */}

              <div className="create-user-row document-row">
                <div className="create-user-field create-user-document-field">
                  <FieldLabel
                    icon={FiUpload}
                  >
                    {t(
                      "fields.idPhoto.label"
                    )}
                  </FieldLabel>

                  <button
                    type="button"
                    className={`create-user-file-box ${
                      invalid.idPhoto
                        ? "input-error"
                        : ""
                    }`}
                    onClick={() =>
                      pickFile(
                        idFileRef
                      )
                    }
                  >
                    <span className="create-user-file-icon">
                      <FiUpload />
                    </span>

                    <span className="create-user-file-copy">
                      <strong>
                        {idFile
                          ? idFile.name
                          : t(
                              "fields.idPhoto.upload"
                            )}
                      </strong>

                      <small>
                        {t(
                          "fields.idPhoto.formats"
                        )}
                      </small>

                      <small>
                        {t(
                          "fields.idPhoto.max_size"
                        )}
                      </small>
                    </span>
                  </button>

                  <input
                    ref={
                      idFileRef
                    }
                    type="file"
                    accept="image/*,.heic,.heif,application/pdf"
                    className="create-user-hidden-input"
                    onChange={
                      handleIdFile
                    }
                  />

                  {idPreview &&
                    idFile && (
                      <a
                        className="create-user-file-preview-link"
                        href={
                          idPreview
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        {idFile.type ===
                        "application/pdf"
                          ? t(
                              "fields.idPhoto.preview_pdf"
                            )
                          : t(
                              "fields.idPhoto.preview_image"
                            )}
                      </a>
                    )}

                  <FieldError
                    value={
                      invalid.idPhoto
                    }
                    fallback={t(
                      "errors.invalid_file"
                    )}
                  />
                </div>

                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiCreditCard}
                  >
                    {t(
                      "fields.idNumber.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiCreditCard
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type="text"
                      value={
                        form.idNumber
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "idNumber",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "idNumber"
                      )} has-leading-icon`}
                      placeholder={t(
                        "fields.idNumber.placeholder"
                      )}
                    />
                  </div>

                  <FieldError
                    value={
                      invalid.idNumber
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>

                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiCalendar}
                  >
                    {t(
                      "fields.idExpiryDate.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiCalendar
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      ref={
                        idExpRef
                      }
                      type="date"
                      value={
                        form.idExpiryDate
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "idExpiryDate",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "idExpiryDate"
                      )} has-leading-icon has-trailing-action`}
                    />

                    <button
                      type="button"
                      className="create-user-input-action"
                      onClick={() =>
                        openPicker(
                          idExpRef
                        )
                      }
                      aria-label={t(
                        "aria.open_calendar"
                      )}
                    >
                      <FiCalendar />
                    </button>
                  </div>

                  <FieldError
                    value={
                      invalid.idExpiryDate
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>
              </div>

              {/* Row 5 */}

              <div className="create-user-row">
                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiHome}
                  >
                    {t(
                      "fields.address.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiHome
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type="text"
                      value={
                        form.address
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "address",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "address"
                      )} has-leading-icon`}
                      placeholder={t(
                        "fields.address.placeholder"
                      )}
                      autoComplete="street-address"
                    />
                  </div>

                  <FieldError
                    value={
                      invalid.address
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>
              </div>

              {/* Row 6 */}

              <div className="create-user-row three">
                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiMapPin}
                  >
                    {t(
                      "fields.postalCode.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiMapPin
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type="text"
                      value={
                        form.postalCode
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "postalCode",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "postalCode"
                      )} has-leading-icon`}
                      placeholder={t(
                        "fields.postalCode.placeholder"
                      )}
                      autoComplete="postal-code"
                    />
                  </div>

                  <FieldError
                    value={
                      invalid.postalCode
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>

                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiMapPin}
                  >
                    {t(
                      "fields.city.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiMapPin
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type="text"
                      value={
                        form.city
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "city",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "city"
                      )} has-leading-icon`}
                      placeholder={t(
                        "fields.city.placeholder"
                      )}
                      autoComplete="address-level2"
                    />
                  </div>

                  <FieldError
                    value={
                      invalid.city
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>

                <div className="create-user-field">
                  <FieldLabel
                    required
                    icon={FiGlobe}
                  >
                    {t(
                      "fields.country.label"
                    )}
                  </FieldLabel>

                  <div className="create-user-input-wrap">
                    <FiGlobe
                      className="create-user-leading-icon"
                      aria-hidden="true"
                    />

                    <input
                      type="text"
                      value={
                        form.country
                      }
                      onChange={(
                        event
                      ) =>
                        set(
                          "country",
                          event
                            .target
                            .value
                        )
                      }
                      className={`${inputClass(
                        "country"
                      )} has-leading-icon`}
                      placeholder={t(
                        "fields.country.placeholder"
                      )}
                      autoComplete="country-name"
                    />
                  </div>

                  <FieldError
                    value={
                      invalid.country
                    }
                    fallback={t(
                      "errors.required"
                    )}
                  />
                </div>
              </div>

              {invalid._server && (
                <div className="create-user-server-error">
                  {
                    invalid._server
                  }
                </div>
              )}
            </main>
          </div>

          <footer className="create-user-actions">
            <button
              type="button"
              className="create-user-btn create-user-btn-ghost"
              onClick={
                closeModal
              }
              disabled={
                submitting
              }
            >
              {t(
                "actions.cancel"
              )}
            </button>

            <button
              type="submit"
              className="create-user-btn create-user-btn-primary"
              disabled={
                submitting
              }
            >
              <FiUserPlus
                aria-hidden="true"
              />

              <span>
                {submitting
                  ? t(
                      "actions.saving"
                    )
                  : t(
                      "actions.create"
                    )}
              </span>
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}